import { inject, Injectable, Injector, computed, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

/**
 * Tracks local (per-component) loading state keyed by a string identifier.
 * Multiple components on the same page each register their own key so they
 * show/hide independently.
 */
@Injectable({ providedIn: 'root' })
export class LocalLoaderService {
  private readonly injector = inject(Injector);
  private readonly counts = new Map<string, number>();
  private readonly activeKeys = signal<Set<string>>(new Set());

  show(key: string): void {
    const count = (this.counts.get(key) ?? 0) + 1;
    this.counts.set(key, count);
    if (count === 1) {
      const next = new Set(this.activeKeys());
      next.add(key);
      this.activeKeys.set(next);
    }
  }

  hide(key: string): void {
    const count = Math.max(0, (this.counts.get(key) ?? 0) - 1);
    this.counts.set(key, count);
    if (count === 0) {
      const next = new Set(this.activeKeys());
      next.delete(key);
      this.activeKeys.set(next);
    }
  }

  /** Returns a signal that is true only while requests tagged with `key` are in-flight. */
  isLoading(key: string) {
    return computed(() => this.activeKeys().has(key));
  }

  /** Observable variant of `isLoading` — for use with `AsyncPipe`. */
  isLoading$(key: string) {
    return toObservable(this.isLoading(key), { injector: this.injector });
  }
}
